const URL_API_KEYS = [
  "continentId",
  "regionId",
  "countryId",
  "countrySlug",
  "stateId",
  "citySlug",
  "keywords",
  "packageIds",
  "packageTypeIds",
  "accommodationTypeIds",
  "travelStyleIds",
  "cityIds",
];

const NUMERIC_URL_KEYS = new Set([
  "continentId",
  "regionId",
  "countryId",
  "stateId",
]);

const ARRAY_URL_KEYS = new Set([
  "packageIds",
  "packageTypeIds",
  "accommodationTypeIds",
  "travelStyleIds",
  "cityIds",
]);

const QUERY_KEY_MAP = {
  continentid: "continentId",
  regionid: "regionId",
  countryid: "countryId",
  countryslug: "countrySlug",
  countryname: "countryName",
  stateid: "stateId",
  statename: "stateName",
  regionid: "regionId",
  regionname: "regionName",
  cityslug: "citySlug",
  cityid: "cityIds",
  cityname: "cityName",
  keywords: "keywords",
  keyword: "keywords",
  month: "month",
  packageids: "packageIds",
  packageid: "packageIds",
  packagetypeid: "packageTypeIds",
  packagetypeids: "packageTypeIds",
  accommodationtypeid: "accommodationTypeIds",
  accommodationtypeids: "accommodationTypeIds",
  travelstyleid: "travelStyleIds",
  travelstyleids: "travelStyleIds",
};

function normalizeQueryKey(key = "") {
  return String(key).replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
}

function slugifyValue(value = "") {
  return String(value)
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");
}

function parseIdList(raw) {
  if (raw == null || raw === "") return [];

  const values = Array.isArray(raw) ? raw : String(raw).split(",");
  return values.map((item) => Number(item)).filter((item) => item > 0);
}

function parseStringValue(raw) {
  const value = Array.isArray(raw) ? raw[0] : raw;
  const text = String(value ?? "").trim();
  return text || "";
}

function setParam(params, key, value) {
  if (value == null) return;
  if (typeof value === "string" && value.trim() === "") return;
  if (Array.isArray(value) && !value.length) return;
  params[key] = value;
}

function mergeIdList(params, key, raw) {
  const next = parseIdList(raw);
  if (!next.length) return;
  params[key] = [...new Set([...(params[key] || []), ...next])];
}

export function compactHolidayListPayload(payload = {}) {
  const result = {};

  Object.entries(payload).forEach(([key, value]) => {
    if (value == null) return;

    if (typeof value === "string" && value.trim() === "") return;

    if (typeof value === "number") {
      if (value === 0) return;
      result[key] = value;
      return;
    }

    if (typeof value === "boolean") {
      if (!value) return;
      result[key] = value;
      return;
    }

    if (Array.isArray(value)) {
      const cleaned = value.filter(
        (item) =>
          item != null &&
          item !== "" &&
          !(typeof item === "number" && item === 0)
      );
      if (!cleaned.length) return;
      result[key] = cleaned;
      return;
    }

    result[key] = value;
  });

  return result;
}

export function parseUrlListParams(query = {}) {
  const params = {};

  Object.entries(query || {}).forEach(([rawKey, rawValue]) => {
    const mappedKey = QUERY_KEY_MAP[normalizeQueryKey(rawKey)];
    if (!mappedKey) return;

    // if (mappedKey === "countryName") {
    //   const name = parseStringValue(rawValue);
    //   if (!name) return;
    //   params.countryName = name;
    //   if (!params.countrySlug) {
    //     setParam(params, "countrySlug", slugifyValue(name));
    //   }
    //   return;
    // }

    if (mappedKey === "cityName") {
      const name = parseStringValue(rawValue);
      if (!name) return;
      params.cityName = name;
      if (!params.citySlug) {
        setParam(params, "citySlug", slugifyValue(name));
      }
      return;
    }

    if (mappedKey === "stateName") {
      const name = parseStringValue(rawValue);
      if (!name) return;
      params.stateName = name;
      return;
    }

    if (mappedKey === "regionName") {
      const name = parseStringValue(rawValue);
      if (!name) return;
      params.regionName = name;
      return;
    }

    if (NUMERIC_URL_KEYS.has(mappedKey)) {
      const parsed = Number(parseStringValue(rawValue));
      if (parsed > 0) params[mappedKey] = parsed;
      return;
    }

    if (ARRAY_URL_KEYS.has(mappedKey)) {
      mergeIdList(params, mappedKey, rawValue);
      return;
    }

    if (mappedKey === "packageIds") {
      mergeIdList(params, "packageIds", rawValue);
      return;
    }

    setParam(params, mappedKey, parseStringValue(rawValue));
  });

  return params;
}

export function pickUrlApiParams(urlParams = {}) {
  const result = {};

  URL_API_KEYS.forEach((key) => {
    if (urlParams[key] == null) return;
    result[key] = urlParams[key];
  });

  return result;
}

const FILTER_QUERY_MAP = {
  continentid: { apiKey: "continentId", type: "number" },
  regionid: { apiKey: "regionId", type: "number" },
  countryid: { apiKey: "countryId", type: "number" },
  stateid: { apiKey: "stateId", type: "number" },
  cityslug: { apiKey: "citySlug", type: "string" },
  activitytypeslug: { apiKey: "activityTypeSlug", type: "string" },
  activitysubtypeslug: { apiKey: "activitySubTypeSlug", type: "string" },
  keywords: { apiKey: "searchText", type: "string" },
  keyword: { apiKey: "searchText", type: "string" },
  activityids: { apiKey: "activityIds", type: "numberArray" },
  activityid: { apiKey: "activityIds", type: "numberArray" },
  packageids: { apiKey: "activityIds", type: "numberArray" },
  packageid: { apiKey: "activityIds", type: "numberArray" },
  pagesize: { apiKey: "pageSize", type: "number" },
  pageindex: { apiKey: "pageIndex", type: "number" },
  activitytypeids: { apiKey: "activityTypeIds", type: "numberArray" },
  activitytypeid: { apiKey: "activityTypeIds", type: "numberArray" },
  packagetypeids: { apiKey: "activityTypeIds", type: "numberArray" },
  packagetypeid: { apiKey: "activityTypeIds", type: "numberArray" },
  subtypeids: { apiKey: "subTypeIds", type: "numberArray" },
  subtypeid: { apiKey: "subTypeIds", type: "numberArray" },
  cityids: { apiKey: "cityIds", type: "numberArray" },
  cityid: { apiKey: "cityIds", type: "numberArray" },
  timeofday: { apiKey: "timeOfDay", type: "stringArray" },
  durations: { apiKey: "durations", type: "stringArray" },
  duration: { apiKey: "durations", type: "stringArray" },
  minprice: { apiKey: "minPrice", type: "number" },
  maxprice: { apiKey: "maxPrice", type: "number" },
  reviewrating: { apiKey: "reviewRating", type: "number" },
  cancellationpolicyids: { apiKey: "cancellationPolicyIds", type: "numberArray" },
  cancellationpolicyid: { apiKey: "cancellationPolicyIds", type: "numberArray" },
  includestypeids: { apiKey: "includesTypeIds", type: "numberArray" },
  includetypeids: { apiKey: "includesTypeIds", type: "numberArray" },
  includesTypeIds: { apiKey: "includesTypeIds", type: "numberArray" },
  instantconfirm: { apiKey: "instantConfirm", type: "string" },
  sortby: { apiKey: "sortBy", type: "string" },
};

function parseStringArray(raw) {
  if (raw == null || raw === "") return [];
  const values = Array.isArray(raw) ? raw : String(raw).split(",");
  return values.map((item) => String(item).trim()).filter(Boolean);
}

function parseFilterQueryValue(raw, type) {
  if (raw == null || raw === "") return undefined;

  switch (type) {
    case "number": {
      const parsed = Number(parseStringValue(raw));
      return Number.isFinite(parsed) ? parsed : undefined;
    }
    case "numberArray": {
      const values = parseIdList(raw);
      return values.length ? values : undefined;
    }
    case "stringArray": {
      const values = parseStringArray(raw);
      return values.length ? values : undefined;
    }
    default: {
      const value = parseStringValue(raw);
      return value || undefined;
    }
  }
}

function mergeFilterPayloadValue(payload, apiKey, value, type) {
  if (value == null) return;

  if (type === "numberArray" || type === "stringArray") {
    const current = Array.isArray(payload[apiKey]) ? payload[apiKey] : [];
    payload[apiKey] = [...new Set([...current, ...value])];
    return;
  }

  payload[apiKey] = value;
}

export function buildHolidayFilterPayload(query = {}) {
  const payload = {
    ...pickUrlApiParams(parseUrlListParams(query)),
  };

  Object.entries(query || {}).forEach(([rawKey, rawValue]) => {
    const config = FILTER_QUERY_MAP[normalizeQueryKey(rawKey)];
    if (!config) return;

    const parsed = parseFilterQueryValue(rawValue, config.type);
    mergeFilterPayloadValue(payload, config.apiKey, parsed, config.type);
  });

  return compactHolidayListPayload(payload);
}

export function getUrlPageTitle(urlParams = {}) {
  if (urlParams.countryName) return urlParams.countryName;
  if (urlParams.cityName) return urlParams.cityName;
  if (urlParams.stateName) return urlParams.stateName;
  if (urlParams.regionName) return urlParams.regionName;
  if (urlParams.countrySlug) return String(urlParams.countrySlug).replace(/-/g, " ");
  if (urlParams.citySlug) return String(urlParams.citySlug).replace(/-/g, " ");
  return "";
}

export function getUrlFilterDefaults(urlParams = {}) {
  return {
    packageTypeIds: urlParams.packageTypeIds || [],
    accommodationTypeIds: urlParams.accommodationTypeIds || [],
    travelStyleIds: urlParams.travelStyleIds || [],
    cityIds: urlParams.cityIds || [],
    availableMonths: urlParams.month ? [urlParams.month] : [],
  };
}

function asOptions(list, labelKey = "label", idKey = "id") {
  if (!Array.isArray(list)) return [];

  return list
    .map((item, index) => {
      if (item == null) return null;

      if (typeof item === "string") {
        return { id: item, label: item, value: item, raw: item };
      }

      const id = item[idKey] ?? item.id ?? item.value ?? item.slug ?? index;
      const label =
        item[labelKey] ??
        item.label ??
        item.title ??
        item.name ??
        item.packageTypeName ??
        item.value ??
        String(id);

      return {
        id,
        label,
        value: item.value ?? id,
        icon: item.icon || "",
        slug: item.slug || "",
        packageCount: item.packageCount ?? null,
        payloadKey: item.payloadKey || item.key || item.filterKey || "",
        payloadValue: item.payloadValue ?? item.value ?? item.filterValue ?? "",
        raw: item,
      };
    })
    .filter(Boolean);
}

function asCityOptions(list = []) {
  if (!Array.isArray(list)) return [];

  return list
    .map((item, index) => {
      if (!item || typeof item !== "object") return null;
      const id = item.cityId ?? item.id ?? index;
      return {
        id,
        label: item.label ?? item.cityName ?? item.name ?? String(id),
        value: id,
        slug: item.slug || "",
        packageCount: item.packageCount ?? null,
        raw: item,
      };
    })
    .filter(Boolean);
}

function asPackageTypeOptions(list = []) {
  if (!Array.isArray(list)) return [];

  return list
    .map((item, index) => {
      if (!item || typeof item !== "object") return null;
      const id = item.packageTypeId ?? item.id ?? index;
      return {
        id,
        label: item.label ?? item.packageTypeName ?? item.name ?? String(id),
        value: id,
        icon: item.icon || "",
        slug: item.slug || "",
        packageCount: item.packageCount ?? null,
        raw: item,
      };
    })
    .filter(Boolean);
}

function readTripDuration(source = {}) {
  return {
    min: Number(source.minNights ?? source.min ?? 0) || 0,
    max: Number(source.maxNights ?? source.max ?? 0) || 0,
    options: asOptions(source.options, "label", "value"),
  };
}

function readBudget(source = {}, fallbackCurrency = "") {
  return {
    min: Number(source.minPrice ?? source.min ?? 0) || 0,
    max: Number(source.maxPrice ?? source.max ?? 0) || 0,
    currency: source.currency || fallbackCurrency || "",
  };
}

export function normalizeHolidayFilters(response) {
  const data = response?.data || response || {};

  const tripDuration = readTripDuration(data.tripDuration || data.nights || data.duration);
  const budget = readBudget(data.budget || data.price || data.priceRange, data.currency);

  return {
    originCountry: data.originCountry || "",
    currency: data.currency || budget.currency || "",
    totalPackages: Number(data.totalPackages) || 0,
    tripDuration,
    nights: { min: tripDuration.min, max: tripDuration.max },
    nightOptions: tripDuration.options,
    budget,
    price: { min: budget.min, max: budget.max, currency: budget.currency },
    flights: asOptions(data.flights, "label", "value"),
    packageTypes: asPackageTypeOptions(data.packageTypes || data.themes || data.packageType),
    accommodationTypes: asOptions(
      data.accommodationTypes || data.accommodationType || data.accommodations
    ),
    travelStyles: asOptions(data.travelStyles || data.travelStyle),
    cities: asCityOptions(data.cities || data.cityList),
    reviewRatings: asOptions(
      data.reviewRatings || data.reviewRating || data.reviewRatingOptions,
      "label",
      "value"
    ),
    hotelRatings: asOptions(
      data.hotelRatings || data.hotelRating || data.hotelRatingOptions,
      "label",
      "value"
    ),
    cancellationPolicies: asOptions(
      data.cancellationPolicies || data.cancellationPolicy || data.fixedDepartures
    ),
    visaIncluded: asOptions(data.visaIncluded, "label", "value"),
    activityIncluded: asOptions(
      data.activityIncluded || data.includesTypeIds,
      "label",
      "value"
    ),
    availableMonths: asOptions(data.availableMonths || data.months, "label", "value"),
    sortOptions: asOptions(data.sortBy || data.sortOptions, "label", "value"),
    title:
      data.pageTitle ||
      data.destinationName ||
      data.countryName ||
      data.cityName ||
      "",
  };
}

export function buildHolidayListPayload({
  urlParams = {},
  filterState = {},
  pageIndex = 1,
  pageSize = 10,
  sortBy = "",
} = {}) {
  const payload = {
    ...pickUrlApiParams(urlParams),
    pageIndex,
    pageSize,
  };

  const mergeArrayParam = (key, values = []) => {
    if (!values.length) return;
    payload[key] = [...new Set([...(payload[key] || []), ...values])];
  };

  mergeArrayParam("packageTypeIds", filterState.packageTypeIds);
  mergeArrayParam("accommodationTypeIds", filterState.accommodationTypeIds);
  mergeArrayParam("travelStyleIds", filterState.travelStyleIds);
  mergeArrayParam("cityIds", filterState.cityIds);
  mergeArrayParam("cancellationPolicyIds", filterState.cancellationPolicyIds);
  mergeArrayParam("availableMonths", filterState.availableMonths);
  if (urlParams.month) {
    mergeArrayParam("availableMonths", [urlParams.month]);
  }
  mergeArrayParam("packageIds", filterState.packageIds);
  mergeArrayParam("nights", filterState.nights);
  mergeArrayParam("flights", filterState.flights);
  if (filterState.minPrice > 0) {
    payload.minPrice = filterState.minPrice;
  }
  if (filterState.maxPrice > 0) {
    payload.maxPrice = filterState.maxPrice;
  }
  if (filterState.reviewRating > 0) {
    payload.reviewRating = filterState.reviewRating;
  }
  mergeArrayParam("hotelRating", filterState.hotelRating);
  if (filterState.visaIncluded) {
    payload.visaIncluded = filterState.visaIncluded;
  }
  if (filterState.activityIncluded) {
    payload.activityIncluded = filterState.activityIncluded;
  }

  if (sortBy && sortBy !== "recommended") {
    payload.sortBy = sortBy;
  }

  return compactHolidayListPayload(payload);
}

export function expandNightRange(min, max) {
  if (!Number.isFinite(min) || !Number.isFinite(max) || min <= 0 || max <= 0) {
    return [];
  }

  const start = Math.min(min, max);
  const end = Math.max(min, max);
  const nights = [];

  for (let value = start; value <= end; value += 1) {
    nights.push(value);
  }

  return nights;
}

export function buildFilterState({
  packageTypeIds = [],
  accommodationTypeIds = [],
  travelStyleIds = [],
  cityIds = [],
  cancellationPolicyIds = [],
  availableMonths = [],
  flights = [],
  nightsRange = [],
  nightOptionValues = [],
  budgetRange = [],
  reviewRating = 0,
  hotelRatings = [],
  visaIncluded = "",
  activityIncluded = "",
  filterBounds = {},
}) {
  const [nightMin, nightMax] = nightsRange;
  const [budgetMin, budgetMax] = budgetRange;
  const nights = [];

  if (nightOptionValues.length) {
    nights.push(...nightOptionValues);
  } else if (
    nightMin > 0 &&
    nightMax > 0 &&
    (nightMin !== filterBounds.nightMin || nightMax !== filterBounds.nightMax)
  ) {
    nights.push(...expandNightRange(nightMin, nightMax));
  }

  const state = {
    packageTypeIds,
    accommodationTypeIds,
    travelStyleIds,
    cityIds,
    cancellationPolicyIds,
    availableMonths,
    flights,
    reviewRating,
  };

  const normalizedHotelRatings = (Array.isArray(hotelRatings) ? hotelRatings : [])
    .map((value) => Number(value))
    .filter((value) => value > 0);
  if (normalizedHotelRatings.length) {
    state.hotelRating = [...new Set(normalizedHotelRatings)];
  }

  if (nights.length) state.nights = nights;

  if (budgetMax > 0 && budgetMax !== filterBounds.budgetMax) {
    state.maxPrice = budgetMax;
  }
  if (budgetMin > 0 && budgetMin !== filterBounds.budgetMin) {
    state.minPrice = budgetMin;
  }

  if (visaIncluded) state.visaIncluded = visaIncluded;
  if (activityIncluded) state.activityIncluded = activityIncluded;

  return state;
}

export function buildReviewBands(reviewRatings = []) {
  if (!reviewRatings.length) return [];

  return reviewRatings.map((item) => ({
    id: item.id,
    label: item.label,
    min: Number(item.raw?.value ?? item.value ?? item.id) || 0,
  }));
}
